import * as React from 'react'

import { useApolloClient, useQuery } from '@apollo/client'
import { Button, SelectField } from '@factorialco/deprecated-design-system'
import {
  LocationWithEmployeesDocument,
  LocationsEmployeesCollectionListDocument,
  UpdateEmployeeWithLocationDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { useDeleteLocation } from 'modules/locations/hooks/useDeleteLocation'
import { useLocations } from 'modules/locations/hooks/useLocations'
import { useUpdateLocation } from 'modules/locations/hooks/useUpdateLocation'

interface Props {
  locationId: number
  onClose: () => void
}

const RemoveLocationModal = ({ locationId, onClose }: Props) => {
  const { deleteLocation } = useDeleteLocation()
  const { updateLocation } = useUpdateLocation()
  const { locations } = useLocations()
  const [selectedLocationId, setSelectedLocationId] = React.useState<string | undefined>(undefined)
  const [selectedHeadquarterId, setSelectedHeadquarterId] = React.useState<string | undefined>(
    undefined
  )
  const apolloClient = useApolloClient()

  const { loading, data } = useQuery(LocationWithEmployeesDocument, {
    variables: { ids: [locationId] },
    fetchPolicy: 'network-only',
  })

  const options = locations
    .filter((loc) => locationId !== loc.id)
    .map((item) => ({
      label: item.name,
      value: item.id,
    }))

  const location = data?.locations.locationsConnection.nodes[0]

  if (!location) return null

  const hasEmployees = location ? location.employees.length > 0 : false

  const description = (): string => {
    if (!location) return ''

    if (hasEmployees && location.main) {
      return i18n.t('settings.locations.remove.with_employees_and_headquarter')
    } else if (hasEmployees && !location.main) {
      return i18n.t('settings.locations.remove.with_employees')
    }

    return i18n.t('settings.locations.remove.description')
  }

  const disabled = (): boolean => {
    return (
      locations.length < 2 ||
      (!!hasEmployees && !selectedLocationId) ||
      (location.main && !selectedHeadquarterId)
    )
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!location) return null

    const employees = location.employees

    const isMainLocation = location.main

    const newMainLocation = locations.find((item) => item.id == Number(selectedHeadquarterId))

    if (isMainLocation && newMainLocation) {
      await updateLocation({ id: newMainLocation.id, main: true })
    }

    const promises = employees.map((employee) =>
      apolloClient.mutate({
        mutation: UpdateEmployeeWithLocationDocument,
        variables: {
          employeeId: employee.id,
          locationId: Number(selectedLocationId),
        },
        refetchQueries: [LocationsEmployeesCollectionListDocument],
      })
    )

    await asyncAction(() => Promise.all(promises), {
      successFlash: false,
    })

    await deleteLocation({ id: location.id })
    closePortal()
  }

  if (loading || !location) return null

  return (
    <Modal
      illustration="location-pin-remove"
      title={i18n.t('settings.locations.remove.title', {
        name: location.name,
      })}
      description={description()}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={disabled()} type="negative" submit>
          {i18n.t('settings.locations.remove.submit')}
        </Button>
      }
    >
      {location.main && (
        <FormRow>
          <SelectField
            none={i18n.t('common.none')}
            value={selectedHeadquarterId}
            onChange={(value) => setSelectedHeadquarterId(value)}
            label={i18n.t('common.new_headquarter')}
            options={options}
            missingLabel={i18n.t('common.missing_field')}
            selectValue={i18n.t('common.select')}
          />
        </FormRow>
      )}
      {hasEmployees && (
        <FormRow>
          <SelectField
            none={i18n.t('common.none')}
            value={selectedLocationId}
            onChange={(value) => setSelectedLocationId(value)}
            label={i18n.t('common.location')}
            options={options}
            missingLabel={i18n.t('common.missing_field')}
            selectValue={i18n.t('common.select')}
          />
        </FormRow>
      )}
    </Modal>
  )
}

export default observer(RemoveLocationModal)
